import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  TableCellsIcon,
  ChartBarIcon,
  EyeIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/20/solid';
import DisclaimerFooter from '../DisclaimerFooter';

const PlatformGuidePage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const platformFeatures = [
    {
      id: 'models-overview',
      name: 'Model Portfolios Overview',
      description: 'Access and analyze our systematic model portfolios with detailed performance metrics.',
      icon: ChartBarIcon,
      details: `The Models section provides comprehensive information about each of our systematic portfolios:

      • Performance Metrics: View historical returns, volatility, and Sharpe ratios
      • Holdings Analysis: See current positions and recent changes
      • Risk Analytics: Understand factor exposures and sector allocations
      • Benchmark Comparisons: Track performance versus relevant indices

      Each model page includes:
      • Monthly performance data and charts
      • Current portfolio composition
      • Recent trades and turnover statistics
      • Risk metrics and factor exposures`
    },
    {
      id: 'power-list',
      name: 'Power List™ Rankings',
      description: 'Our proprietary stock rankings combining all signals into actionable investment ideas.',
      icon: TableCellsIcon,
      details: `The Power List™ is our comprehensive ranking system that scores every stock in our universe:

      • Overall Scores: Combined signal strength from 1-100
      • Individual Signal Scores: See how each stock ranks on specific factors
      • Filtering and Screening: Filter by market cap, sector, or specific signal strength
      • Historical Performance: Track how rankings have changed over time

      Use the Power List™ to:
      • Generate investment ideas based on our signals
      • Screen for stocks meeting specific criteria
      • Build custom portfolios using our rankings
      • Understand why our models hold specific positions`
    },
    {
      id: 'watchlists',
      name: 'Watchlists and Analysis',
      description: 'Create custom watchlists and analyze how your holdings compare to our signals.',
      icon: EyeIcon,
      details: `The Watchlist feature allows you to track specific stocks and analyze them using our signals:

      • Custom Lists: Create watchlists for different strategies or client needs
      • Signal Analysis: See how your holdings rank on our various signals
      • Performance Tracking: Monitor how your watchlist performs over time
      • Comparison Tools: Compare your selections to our model portfolios

      Monthly insights include:
      • Signal strength changes for your holdings
      • Suggested additions or removals based on signal updates
      • Performance attribution showing which signals drove returns
      • Risk analysis of your custom portfolio`
    },
    {
      id: 'monthly-rebalance',
      name: 'Monthly Rebalancing Process',
      description: 'Understanding our systematic monthly portfolio updates and how to implement them.',
      icon: CalendarIcon,
      details: `Our portfolios rebalance monthly using a systematic, rules-based process:

      Monthly Schedule:
      • Signal Generation: We update all signals using the latest available data
      • Portfolio Construction: New portfolios are built using updated signals and current market conditions
      • Trade Lists: We generate specific buy/sell recommendations for each model
      • Implementation Support: We provide guidance on executing the rebalancing trades

      What you receive each month:
      • Updated portfolio weights for each model
      • Trade lists showing exactly what to buy and sell
      • Performance attribution explaining recent returns
      • Market commentary highlighting key signal changes

      Implementation tips:
      • Plan trades for the first few trading days of each month
      • Use our liquidity guidelines to minimize market impact
      • Consider transaction costs when implementing smaller position changes
      • Monitor execution quality and report any issues to our team`
    }
  ];

  return (
    <div className="min-h-screen bg-bg-100">
      <div className="px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <div className="flex items-center text-sm text-text-300">
            <Link to="/learn" className="hover:text-primary-100 transition-colors">
              Learn
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" />
            <span className="text-text-100">Platform Guide</span>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-100 mb-4 flex items-center gap-3">
            <TableCellsIcon className="h-8 w-8 text-primary-100" />
            Platform Guide
          </h1>
          <p className="text-lg text-text-200">
            Comprehensive guide to using our platform tools effectively for investment research and portfolio management.
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-8 bg-bg-200 rounded-lg p-6 border border-bg-300">
          <h2 className="text-xl font-semibold text-text-100 mb-4">Getting the Most from Our Platform</h2>
          <p className="text-text-200 leading-relaxed">
            Our platform is designed to provide investment professionals with the tools and insights needed to make informed decisions. 
            From comprehensive model analytics to custom research capabilities, each feature is built to enhance your investment process 
            and provide actionable insights for your clients.
          </p>
        </div>

        {/* Platform Features */}
        <div className="space-y-4">
          {platformFeatures.map((feature) => (
            <div key={feature.id} className="bg-bg-200 rounded-lg border border-bg-300">
              <button
                onClick={() => toggleSection(feature.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-bg-300 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <feature.icon className="h-5 w-5 text-primary-100" />
                  <div>
                    <h3 className="font-semibold text-text-100">{feature.name}</h3>
                    <p className="text-sm text-text-200 mt-1">{feature.description}</p>
                  </div>
                </div>
                {expandedSections[feature.id] ? (
                  <ChevronDownIcon className="h-5 w-5 text-text-200" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-text-200" />
                )}
              </button>
              {expandedSections[feature.id] && (
                <div className="px-6 pb-4">
                  <div className="text-text-200 leading-relaxed whitespace-pre-line">
                    {feature.details}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Best Practices */}
        <div className="mt-8 bg-accent-100/5 rounded-lg p-6 border border-accent-100/20">
          <h3 className="text-lg font-semibold text-text-100 mb-4">Platform Best Practices</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-text-100 mb-2">Regular Review Cycle</h4>
              <p className="text-sm text-text-200">
                Establish a monthly review process to analyze model performance, update watchlists, and identify new opportunities.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-text-100 mb-2">Custom Screening</h4>
              <p className="text-sm text-text-200">
                Use the Research page filters to create custom screens that align with your specific investment criteria and client needs.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-text-100 mb-2">Signal Integration</h4>
              <p className="text-sm text-text-200">
                Combine multiple signals and factors to build a comprehensive view of investment opportunities and risks.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-text-100 mb-2">Performance Attribution</h4>
              <p className="text-sm text-text-200">
                Regularly analyze which signals and factors are driving performance to refine your investment approach.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
};

export default PlatformGuidePage; 